import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  TemplateRef,
  HostListener,
  AfterViewInit,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router } from "@angular/router";
import { formatNumber, NgClass, NgStyle, NgTemplateOutlet } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { MapSizeService } from "../../../modules/map-module/map-services/MapSizeService.service";
import { TemplateService } from "../../../services/shared/TemplateService.service";
import { HomeService } from "../home/home.service";
import { HomeControllerService } from "../../../services/server/HomeController.service";
import { MapControllerComponent } from "../../../modules/map-module/map-controller/map-controller.component";
import { Client } from "../../../interfaces/ClientGlobalInterface.type";
import { GlobalEntityData } from "../home/HomeInterface.type";
import { RANDOM_COLORS } from "../../../modules/map-module/map-variables/MAP_COLORS";
import { THEME_COLORS } from "../../../themes/THEME_COLORS";

/**
 * Client-level home showing all entities on a map with their summary cards.
 * Selecting an entity switches the session to its virtual agrupation; Tab navigates to the agrupation home.
 */
@Component({
  selector: "app-home-global",
  templateUrl: "./home-global.component.html",
  styleUrls: ["./home-global.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, NgStyle, MapControllerComponent, TranslateModule, NgTemplateOutlet]
})
export class HomeGlobalComponent implements OnInit, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  elseBlock: TemplateRef<any> | null = this.TemplateService.get("elseBlock");

  // Session data stream
  currentClient: Client;
  formatNumber = formatNumber;
  numberFormat: string;
  RANDOM_COLORS = RANDOM_COLORS;
  currentColors: object;
  readonly THEME_COLORS = THEME_COLORS;

  // Entity array
  homeGlobalEntities: GlobalEntityData[];

  // Map
  mapType: string = "homeGlobal";
  @ViewChild("homePanel") homePanel: ElementRef;
  @ViewChild("mapContainer") mapContainer: ElementRef;
  mapHeight: number;
  mapPadding: number = 0;
  @ViewChild(MapControllerComponent) mapController: MapControllerComponent;

  @HostListener("window:resize")
  onResize() {
    this.resizeMap();
  }

  @HostListener("document:keydown", ["$event"])
  handleKeyboardDownEvent(event: KeyboardEvent) {
    if (event.code === "Tab") {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.router.navigate(["/principal-agrupacion"]);
    }
  }

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly HomeController: HomeControllerService,
    public HomeService: HomeService,
    private readonly MapSizeService: MapSizeService,
    private readonly router: Router,
    private readonly SessionDataService: SessionDataService,
    private readonly TemplateService: TemplateService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.currentColors = untracked(() => this.SessionDataService.colors());
    this.currentClient = untracked(() => this.SessionDataService.client());
    this.numberFormat = untracked(() => this.SessionDataService.numberFormat());

    if (this.currentClient) {
      this.loadComponent();
    }
  }

  /***************************************************************************/
  // ANCHOR After-render execution
  /***************************************************************************/
  ngAfterViewInit(): void {
    if (this.currentClient) {
      // Wait until the map container is laid out before measuring it.
      setTimeout(() => this.resizeMap(), 0);
    }
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  loadComponent(): void {
    this.getMapData();
  }

  getMapData(): void {
    let entities: GlobalEntityData[] = [];

    this.HomeController.getGlobalEntityData(this.currentClient.clientId).subscribe(response => {
      if (response["code"] === 0) {
        entities = response["body"];
        entities.forEach(entity => {
          let clientEntity = this.currentClient.entityList.find(
            clientEntity => clientEntity.id === entity.id
          );
          let entityVirtual = clientEntity.agrupations.find(agrupation => agrupation.virtual);
          entity.virtual = entityVirtual || clientEntity.agrupations[0];
          entity.show = true;
        });
        this.homeGlobalEntities = entities.sort((a, b) => a.entity.localeCompare(b.entity));
      }
    });
  }

  resizeMap(): void {
    this.mapHeight =
      window.innerWidth > 980
        ? this.MapSizeService.calcMapHeight(this.homePanel, null, this.mapPadding)
        : 600;
  }

  goToEntity(entity: GlobalEntityData): void {
    this.SessionDataService.sendAgrupation(entity.virtual);
    this.router.navigate(["principal-agrupacion"]);
  }

  highlightMapEntity(entity?: GlobalEntityData): void {
    this.homeGlobalEntities.forEach(globalEntity => (globalEntity.show = !entity));
    if (entity) {
      entity.show = true;
    }
    this.mapController.showEntityTooltip(entity?.id);
  }

  highlightTableEntity(entity?: GlobalEntityData): void {
    this.homeGlobalEntities.forEach(globalEntity => (globalEntity.show = !entity));
    if (entity) {
      entity.show = true;
      let entityElement = document.getElementById("entity" + entity.id);
      if (entityElement) {
        entityElement.scrollIntoView({
          behavior: "auto",
          block: "center",
          inline: "nearest"
        });
      }
    }
  }
}
